import { batch } from "solid-js";
import { reconcile } from "solid-js/store";

import { Delete, Get, Post } from "@repo/utils/api";
import Chainable from "@repo/utils/Chainable";
import UProxy from "@repo/utils/UProxy";
import FeatureStore from "@repo/utils-solid/FeatureStore";
import Membership from "@repo/vygor-models/Membership";

import { api } from "..";

import { Customer, useCustomers } from "./customers";
import { Organization, useOrganizations } from "./organizations";
import { PaymentMethod } from "./paymentMethods";
import { Promotion } from "./promotions";

export { Membership };

export type MembershipsRest = Post<
  {
    membershipPlanId: Membership.Plan.Id;
    paymentMethodId: PaymentMethod.Id;
    promoCodeId?: Promotion.Code.Id;
  },
  { membership: Membership; customer: Customer }
> & {
  [id: Membership.Id]: Get<
    {},
    {
      membership: Membership;
      membershipPlan: Membership.Plan;
      organization: Organization;
    }
  > &
    Delete<{}, {}>;
};

export type MembershipPlansRest = {
  [id: Membership.Plan.Id]: Get<
    { asAdmin?: boolean },
    {
      membershipPlan: Membership.Plan.FromApi;
      membershipPlanSupplement?: Membership.Plan.Supplement;
    }
  > & {
    memberships: Get<
      { status?: "active"; page?: number },
      {
        memberships: Membership[];
        customers: Customer[];
      }
    >;
  };
};

const initialState = {
  byId: {} as Record<Membership.Id, Membership>,
  plansById: {} as Record<Membership.Plan.Id, Membership.Plan>,
  planSupplementsById: {} as Record<
    Membership.Plan.Id,
    Membership.Plan.Supplement
  >,
  activeMembershipsByPlanId: {} as Record<
    Membership.Plan.Id,
    Membership.Id[] & { page: number }
  >,
};

export const { Context, Provider, useMemberships } = FeatureStore.init(
  "Memberships",
  initialState,
  ([store, setStore], { withLogger, attachAction }) => {
    const [, setCustomers] = useCustomers();
    const [, setOrganizations] = useOrganizations();

    const accessors = {
      byId: attachAction(
        (id: Membership.Id | undefined) =>
          id &&
          store.byId[id] &&
          UProxy.merge(store.byId[id], {
            get name() {
              return accessors.planFor(id)?.name;
            },
            get plan() {
              return accessors.planById(store.byId[id]!.planId);
            },
          }),
        (id) => id && actions.fetch(id),
        (id) => () => !id || store.byId[id],
      ),
      planFor: attachAction(
        (id: Membership.Id | undefined) =>
          id && store.plansById[store.byId[id]?.planId!],
        (id) =>
          id && store.byId[id] && actions.fetchPlan(store.byId[id].planId),
      ),
      planById: Object.assign(
        attachAction(
          (id: Membership.Plan.Id | undefined) => id && store.plansById[id],
          (id) => id && actions.fetchPlan(id),
          (id) => () => !id || store.plansById[id],
        ),
        {
          admin: attachAction(
            (id: Membership.Plan.Id | undefined) =>
              id &&
              store.plansById[id] &&
              UProxy.merge(
                store.plansById[id],
                UProxy.merge(store.planSupplementsById[id], {
                  get activeMemberships() {
                    return accessors.activeMembershipsForPlan(id);
                  },
                }),
              ),
            (id) => id && actions.fetchPlan.asAdmin(id),
            (id) => () => !id || store.planSupplementsById[id],
          ),
        },
      ),
      activeMembershipsForPlan: attachAction(
        (id: Membership.Plan.Id | undefined) =>
          id &&
          store.activeMembershipsByPlanId[id]?.map((id) => store.byId[id]!),
        (id) => id && actions.fetchActiveMembershipsForPlan(id),
      ),
    };

    const mutations = {
      one(membership: Membership) {
        setStore("byId", membership.id, reconcile(membership));
      },
      many(memberships: Membership[]) {
        batch(() => {
          for (const membership of memberships) {
            mutations.one(membership);
          }
        });
      },
      remove(id: Membership.Id) {
        setStore("byId", ({ [id]: _, ...rest }) => rest);
      },
      onePlan(plan: Membership.Plan) {
        setStore("plansById", plan.id, reconcile(plan));
      },
      onePlanSupplement(supplement: Membership.Plan.Supplement) {
        setStore("planSupplementsById", supplement.id, reconcile(supplement));
      },
      manyPlanSupplements(supplements: Membership.Plan.Supplement[]) {
        batch(() => {
          for (const supplement of supplements) {
            mutations.onePlanSupplement(supplement);
          }
        });
      },
      manyPlans(plans: Membership.Plan[]) {
        batch(() => {
          for (const plan of plans) {
            mutations.onePlan(plan);
          }
        });
      },
    };

    const actions = withLogger("action", {
      one: mutations.one,
      many: mutations.many,
      onePlan: mutations.onePlan,
      manyPlans: mutations.manyPlans,
      onePlanSupplement: mutations.onePlanSupplement,
      manyPlanSupplements: mutations.manyPlanSupplements,
      create(
        membershipPlanId: Membership.Plan.Id,
        paymentMethodId: PaymentMethod.Id,
        promoCodeId?: Promotion.Code.Id,
      ) {
        return api.memberships
          .POST({ membershipPlanId, paymentMethodId, promoCodeId })
          .then(async (res) => {
            const { membership, customer } = res;
            batch(() => {
              mutations.one(membership);
              setCustomers.one(customer);
            });
            return res;
          });
      },
      cancel(id: Membership.Id) {
        return api.memberships[id]!.DELETE({}).then(() => {
          batch(() => {
            setCustomers.removeMembership(accessors.byId(id)!);
            mutations.remove(id);
          });
        });
      },
      fetch(id: Membership.Id) {
        return api.memberships[id]!.GET({}).then(
          ({ membership, membershipPlan, organization }) => {
            batch(() => {
              mutations.one(membership);
              mutations.onePlan(membershipPlan);
              setOrganizations.one(organization);
            });
          },
        );
      },
      fetchPlan: Chainable.create(
        Chainable.wrapFactory(
          ({ asAdmin = false }) =>
            (id: Membership.Plan.Id) =>
              api["membership-plans"]
                [id]!["GET"]({ asAdmin })
                .then(({ membershipPlan, membershipPlanSupplement }) => {
                  batch(() => {
                    const normalized =
                      Membership.Plan.normalize(membershipPlan);
                    mutations.onePlan(normalized);
                    if (membershipPlanSupplement)
                      mutations.onePlanSupplement(membershipPlanSupplement);
                  });
                }),
        ),
        { asAdmin: { asAdmin: true } },
      ),
      fetchActiveMembershipsForPlan: Chainable.create(
        Chainable.wrapFactory(
          ({ more = false }) =>
            (planId: Membership.Plan.Id) => {
              const page =
                !more || !store.activeMembershipsByPlanId[planId]
                  ? 0
                  : store.activeMembershipsByPlanId[planId].page + 1;

              return api["membership-plans"]
                [planId]!["memberships"]!.GET({ status: "active", page })
                .then(({ memberships, customers }) => {
                  batch(() => {
                    mutations.many(memberships);
                    setCustomers.many(customers);
                    setStore("activeMembershipsByPlanId", planId, (prev) =>
                      Object.assign(
                        [...(prev || []), ...memberships.map(({ id }) => id)],
                        { page },
                      ),
                    );
                  });
                  return memberships;
                });
            },
        ),
        { more: { more: true } },
      ),
    });

    return { accessors, actions };
  },
);
